import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Block, Close, Delete, Edit, ExpandMore, InfoOutlined, RestoreFromTrash } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import ConfirmModal from "../components/modals/ConfirmModal";
import HealthPlanModal from "../components/modals/HealthPlanModal";
import { Toast } from "../components/Toast";
import SavingOverlay from "../components/SavingOverlay";
import { useToast } from "../hooks/useToast";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, forms, typography } from "../theme/designSystem";
import { filterFieldSx, searchFieldSx } from "../theme/filterStyles";
import { maskDocument } from "../utils/document";
import {
  AGREEMENT_KIND_LABEL,
  AGREEMENT_PAYER_LABEL,
  AgreementKind,
  AgreementPayer,
  formatValidity,
  healthPlanOperatorsService,
  HealthPlanOperatorRow,
  SaveHealthPlanOperatorPayload,
  healthPlansService,
  HealthPlanRow,
  HealthPlanOperatorOption,
} from "../services/healthPlansService";

/**
 * Serviços e convênios → Convênios (RN-411a): operadoras de plano de saúde e empresas conveniadas
 * da entidade. Registro ANS só na operadora; quem paga e a comprovação do vínculo só na empresa.
 * Convênio com planos não se exclui — inativa (a lista mostra quantos planos cada um tem).
 */

interface FormState {
  id: string | null;
  kind: AgreementKind;
  tradeName: string;
  ansRegistration: string;
  providerCode: string;
  document: string;
  companyName: string;
  paymentDeadlineDays: string;
  contactName: string;
  phone: string;
  email: string;
  startDate: string;
  endDate: string;
  payer: "" | AgreementPayer;
  eligibilityProof: string;
  notes: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  id: null, kind: "operator", tradeName: "", ansRegistration: "", providerCode: "", document: "", companyName: "",
  paymentDeadlineDays: "", contactName: "", phone: "", email: "", startDate: "", endDate: "", payer: "",
  eligibilityProof: "", notes: "", active: true,
};

const digits = (v: string) => v.replace(/\D/g, "");
const orNull = (v: string) => (v.trim() ? v.trim() : null);

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

// Rodapé de modal do design system: separado por borda, fundo claro
const dialogActionsSx = {
  padding: "1.5rem 2rem",
  borderTop: `1px solid ${colors.backgroundAlt}`,
  backgroundColor: colors.background,
  gap: "1rem",
};

const toPayload = (f: FormState): SaveHealthPlanOperatorPayload => ({
  id: f.id,
  kind: f.kind,
  tradeName: f.tradeName.trim(),
  ansRegistration: f.kind === "operator" ? digits(f.ansRegistration) : null,
  providerCode: orNull(f.providerCode),
  document: digits(f.document) || null,
  companyName: orNull(f.companyName),
  paymentDeadlineDays: f.paymentDeadlineDays === "" ? null : Number(f.paymentDeadlineDays),
  contactName: orNull(f.contactName),
  phone: orNull(f.phone),
  email: orNull(f.email),
  startDate: f.startDate || null,
  endDate: f.endDate || null,
  payer: f.kind === "company" && f.payer ? f.payer : null,
  eligibilityProof: f.kind === "company" ? orNull(f.eligibilityProof) : null,
  notes: orNull(f.notes),
  active: f.active,
});

const HealthPlanOperators: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { toast, showToast, hideToast } = useToast();

  const [rows, setRows] = useState<HealthPlanOperatorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | AgreementKind>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);

  const [form, setForm] = useState<FormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<HealthPlanOperatorRow | null>(null);
  // Planos do convênio: o plano deixou de ter tela própria e virou filho desta (item 2 do pacote)
  const [plans, setPlans] = useState<HealthPlanRow[]>([]);
  const [operatorOptions, setOperatorOptions] = useState<HealthPlanOperatorOption[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [planStatusFilter, setPlanStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [planForm, setPlanForm] = useState<{ plan: HealthPlanRow | null; operatorId: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const copaymentLabel = (v: boolean | null) => (v === null ? "—" : v ? "Sim" : "Não");

  /** Planos do convênio, já sob o filtro de situação do plano (independente do de convênio). */
  const plansOf = (operatorId: string) =>
    plans.filter((p) => p.operatorId === operatorId
      && (planStatusFilter === "all" || (planStatusFilter === "active" ? p.active : !p.active)));

  const togglePlan = async (plan: HealthPlanRow) => {
    try {
      await healthPlansService.save({
        id: plan.id, operatorId: plan.operatorId, name: plan.name,
        ansProductCode: plan.ansProductCode, copayment: plan.copayment,
        startDate: plan.startDate, endDate: plan.endDate, active: !plan.active,
      });
      await load();
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { description?: string } } })?.response?.data?.description;
      showToast(message || "Erro ao alterar o plano.", "error");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [operadoras, planos] = await Promise.all([
        healthPlanOperatorsService.getAll(),
        healthPlansService.getAll(),
      ]);
      setRows(operadoras);
      setPlans(planos.plans);
      setOperatorOptions(planos.operators);
    } catch {
      showToast("Erro ao carregar os convênios.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Convênios";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    const termDigits = digits(search);
    return rows
      .filter((r) => kindFilter === "all" || r.kind === kindFilter)
      .filter((r) => statusFilter === "all" || (statusFilter === "active" ? r.active : !r.active))
      .filter((r) => {
        if (!term) return true;
        const byText = normalize(`${r.tradeName} ${r.companyName ?? ""} ${r.contactName ?? ""}`).includes(term);
        const byNumber = termDigits.length > 0
          && `${r.ansRegistration ?? ""} ${r.document ?? ""} ${r.providerCode ?? ""}`.includes(termDigits);
        return byText || byNumber;
      });
  }, [rows, search, kindFilter, statusFilter]);

  const hasFilterChanges = search !== "" || kindFilter !== "all" || statusFilter !== "active";
  const clearFilters = () => { setSearch(""); setKindFilter("all"); setStatusFilter("active"); setCurrentPage(1); };

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filtered, currentPage, itemsPerPage],
  );

  // O filtro pode deixar o usuário numa página que não existe mais
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const openEdit = (row: HealthPlanOperatorRow) => setForm({
    id: row.id,
    kind: row.kind,
    tradeName: row.tradeName,
    ansRegistration: row.ansRegistration ?? "",
    providerCode: row.providerCode ?? "",
    document: maskDocument(row.document),
    companyName: row.companyName ?? "",
    paymentDeadlineDays: row.paymentDeadlineDays === null ? "" : String(row.paymentDeadlineDays),
    contactName: row.contactName ?? "",
    phone: row.phone ?? "",
    email: row.email ?? "",
    startDate: row.startDate ?? "",
    endDate: row.endDate ?? "",
    payer: row.payer ?? "",
    eligibilityProof: row.eligibilityProof ?? "",
    notes: row.notes ?? "",
    active: row.active,
  });

  const save = async () => {
    if (!form) return;
    if (!form.tradeName.trim()) { showToast("Informe o nome do convênio.", "error"); return; }
    if (form.kind === "operator" && digits(form.ansRegistration).length !== 6) { showToast("O registro ANS tem 6 dígitos.", "error"); return; }
    if (form.kind === "company" && !form.payer) { showToast("Informe quem paga o atendimento.", "error"); return; }
    const doc = digits(form.document);
    if (doc && doc.length !== 14) { showToast("O CNPJ tem 14 dígitos.", "error"); return; }
    if (form.startDate && form.endDate && form.endDate < form.startDate) { showToast("O fim da vigência não pode ser anterior ao início.", "error"); return; }
    setSaving(true);
    try {
      await healthPlanOperatorsService.save(toPayload(form));
      showToast(form.id ? "Convênio atualizado." : "Convênio incluído.", "success");
      setForm(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao salvar o convênio.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: HealthPlanOperatorRow) => {
    try {
      const { plansCount, ...rest } = row;
      await healthPlanOperatorsService.save({ ...rest, active: !row.active });
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao alterar o convênio.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await healthPlanOperatorsService.remove(deleteTarget.id);
      showToast("Convênio excluído.", "success");
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || "Erro ao excluir o convênio.", "error");
    } finally {
      setSaving(false);
    }
  };

  const set = (patch: Partial<FormState>) => setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  const isOperator = form?.kind === "operator";
  const periodInvalid = Boolean(form?.startDate && form?.endDate && form.endDate < form.startDate);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Convênios</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Operadoras e empresas conveniadas, com os planos de cada uma — a seta abre os planos.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Buscar"
                  placeholder="Nome, registro ANS, CNPJ, código ou contato"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  fullWidth
                  sx={searchFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 100 }}
                />
                <TextField
                  select
                  size="small"
                  label="Tipo"
                  value={kindFilter}
                  onChange={(e) => { setKindFilter(e.target.value as typeof kindFilter); setCurrentPage(1); }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="operator">{AGREEMENT_KIND_LABEL.operator}</MenuItem>
                  <MenuItem value="company">{AGREEMENT_KIND_LABEL.company}</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Situação"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value as typeof statusFilter); setCurrentPage(1); }}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </TextField>
                {/* Filtro INDEPENDENTE (decisão do Hilton, 19/09): com status derivado, plano
                    inativo e plano desligado pela operadora são coisas diferentes — um filtro
                    só esconderia qual é qual. */}
                <TextField
                  select
                  size="small"
                  label="Situação do plano"
                  value={planStatusFilter}
                  onChange={(e) => setPlanStatusFilter(e.target.value as typeof planStatusFilter)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Ativos</MenuItem>
                  <MenuItem value="inactive">Inativos</MenuItem>
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <AddButton onClick={() => setForm({ ...EMPTY_FORM })} title="Novo convênio" />
              </div>
            </div>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!loading && (
              <>
                <Box className="c4u-info-box">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography>
                    Cadastre aqui o convênio; os planos dele ficam em Planos. Fora da vigência, o convênio some da escolha no cadastro do paciente. Com planos, não se exclui, só inativa.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <ResultCount shown={filtered.length} total={rows.length} />

                  {filtered.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      {rows.length === 0
                        ? "Nenhum convênio cadastrado — inclua o primeiro no botão acima."
                        : "Nenhum convênio encontrado com os filtros aplicados."}
                    </Typography>
                  ) : (
                    <Box className="admin-plans-table">
                      <Box className="c4u-table__header">
                        <Box className="admin-plans-header-cell" sx={{ flex: "2 1 220px", minWidth: 180 }}>Convênio</Box>
                        <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 170px" }}>Tipo</Box>
                        <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 110px" }}>Registro ANS</Box>
                        <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 140px" }}>Código da clínica</Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 170px" }}>Vigência</Box>
                        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 70px" }} title="Clique na seta do convênio para ver os planos">Planos</Box>
                        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Situação</Box>
                        <Box className="admin-plans-header-cell no-print" sx={{ flex: "0 0 130px", justifyContent: "flex-end" }}>Ações</Box>
                      </Box>

                      <Box className="admin-plans-table-body">
                        {paged.map((row) => (
                          <React.Fragment key={row.id}>
                          <Box className="c4u-table__row">
                            <Box className="admin-plans-cell admin-plans-name" sx={{ flex: "2 1 220px", minWidth: 180, gap: 0.5, flexWrap: "wrap" }} data-label="Convênio">
                              <IconButton
                                size="small"
                                onClick={() => setExpanded((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
                                aria-label={expanded[row.id] ? "Recolher planos" : "Ver planos"}
                                sx={{ padding: "2px", visibility: row.plansCount > 0 ? "visible" : "hidden" }}
                              >
                                <ExpandMore sx={{ fontSize: "1.1rem", transform: expanded[row.id] ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                              </IconButton>
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{row.tradeName}</Typography>
                              {row.companyName && (
                                <Typography sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}>{row.companyName}</Typography>
                              )}
                            </Box>
                            <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 170px" }} data-label="Tipo">
                              <Typography sx={{ fontSize: 13 }}>{AGREEMENT_KIND_LABEL[row.kind]}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 110px" }} data-label="Registro ANS">
                              <Typography sx={{ fontSize: 13 }}>{row.ansRegistration || "—"}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 140px" }} data-label="Código da clínica">
                              <Typography sx={{ fontSize: 13 }}>{row.providerCode || "—"}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 170px" }} data-label="Vigência">
                              <Typography sx={{ fontSize: 13 }}>{formatValidity(row.startDate, row.endDate)}</Typography>
                            </Box>
                            <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 70px" }} data-label="Planos">
                              <Typography sx={{ fontSize: 13 }}>{row.plansCount}</Typography>
                            </Box>
                            <Box className="admin-plans-cell" sx={{ flex: "0 0 100px" }} data-label="Situação">
                              <Chip
                                label={row.active ? "Ativo" : "Inativo"}
                                size="small"
                                sx={{ color: "white", fontWeight: 600, backgroundColor: row.active ? colors.success : "#adb5bd" }}
                              />
                            </Box>
                            <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 130px" }} data-label="Ações">
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                <Tooltip title="Editar convênio">
                                  <IconButton className="c4u-row-action" size="small" onClick={() => openEdit(row)} sx={rowActionSx(colors.primary, colors.primaryHover)}>
                                    <Edit sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={row.active ? "Inativar (os planos dele saem do vínculo; o histórico fica)" : "Reativar convênio"}>
                                  <IconButton
                                    className="c4u-row-action"
                                    size="small"
                                    onClick={() => toggleActive(row)}
                                    sx={rowActionSx(row.active ? "#fd7e14" : colors.success, row.active ? "#e8590c" : "#218838")}
                                  >
                                    {row.active ? <Block sx={{ fontSize: "1rem" }} /> : <RestoreFromTrash sx={{ fontSize: "1rem" }} />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Incluir plano neste convênio">
                                  <span>
                                    <IconButton
                                      className="c4u-row-action"
                                      size="small"
                                      disabled={!row.active}
                                      onClick={() => {
                                        setExpanded((prev) => ({ ...prev, [row.id]: true }));
                                        setPlanForm({ plan: null, operatorId: row.id });
                                      }}
                                      sx={rowActionSx(colors.success, "#218838")}
                                    >
                                      <Add sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {row.plansCount === 0 && (
                                  <Tooltip title="Excluir (convênio sem planos)">
                                    <IconButton className="c4u-row-action" size="small" onClick={() => setDeleteTarget(row)} sx={rowActionSx("#dc3545", "#c82333")}>
                                      <Delete sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          {expanded[row.id] && plansOf(row.id).map((plan) => (
                            /* Plano: linha-filha, recuada, com as mesmas ações da tela antiga */
                            <Box key={plan.id} className="c4u-table__row" sx={{ backgroundColor: "#fbfcfd" }}>
                              <Box className="admin-plans-cell admin-plans-name" sx={{ flex: "2 1 220px", minWidth: 180, pl: 4 }} data-label="Plano">
                                <Typography sx={{ fontSize: 13 }}>{plan.name}</Typography>
                              </Box>
                              <Box className="admin-plans-cell c4u-col--md" sx={{ flex: "0 0 170px" }} data-label="Coparticipação">
                                <Typography sx={{ fontSize: 13 }}>{copaymentLabel(plan.copayment)}</Typography>
                              </Box>
                              <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 110px" }} data-label="Registro ANS">
                                <Typography sx={{ fontSize: 13 }}>{plan.ansProductCode || "—"}</Typography>
                              </Box>
                              <Box className="admin-plans-cell c4u-col--xl" sx={{ flex: "0 0 140px" }} data-label="Pacientes">
                                <Typography sx={{ fontSize: 13 }}>{plan.patientsCount}</Typography>
                              </Box>
                              <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 170px" }} data-label="Vigência">
                                <Typography sx={{ fontSize: 13 }}>{formatValidity(plan.startDate, plan.endDate)}</Typography>
                              </Box>
                              <Box className="admin-plans-cell c4u-col--lg" sx={{ flex: "0 0 70px" }} data-label="" />
                              <Box className="admin-plans-cell" sx={{ flex: "0 0 100px" }} data-label="Situação">
                                <Chip
                                  label={plan.blockedByOperator ? "Inativo pela operadora" : plan.active ? "Ativo" : "Inativo"}
                                  size="small"
                                  title={plan.blockedByOperator
                                    ? "O plano está ativo, mas o convênio está inativo ou fora da vigência."
                                    : undefined}
                                  sx={{
                                    color: "white",
                                    fontWeight: 600,
                                    backgroundColor: plan.blockedByOperator ? "#fd7e14" : plan.active ? colors.success : "#adb5bd",
                                  }}
                                />
                              </Box>
                              <Box className="admin-plans-cell admin-plans-actions no-print" sx={{ flex: "0 0 130px" }} data-label="Ações">
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                  <Tooltip title="Editar plano">
                                    <IconButton className="c4u-row-action" size="small" onClick={() => setPlanForm({ plan, operatorId: row.id })} sx={rowActionSx(colors.primary, colors.primaryHover)}>
                                      <Edit sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={plan.active ? "Inativar plano (sai do vínculo; o histórico fica)" : "Reativar plano"}>
                                    <IconButton
                                      className="c4u-row-action"
                                      size="small"
                                      onClick={() => togglePlan(plan)}
                                      sx={rowActionSx(plan.active ? "#fd7e14" : colors.success, plan.active ? "#e8590c" : "#218838")}
                                    >
                                      {plan.active ? <Block sx={{ fontSize: "1rem" }} /> : <RestoreFromTrash sx={{ fontSize: "1rem" }} />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {filtered.length > 0 && (
                    <StandardPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={filtered.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                    />
                  )}
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={form !== null} onClose={() => setForm(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? "Editar convênio" : "Novo convênio"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr 1fr" }, columnGap: forms.fieldGapX, rowGap: forms.fieldGapY, alignItems: "start" }}>
              <TextField
                select
                label="Tipo"
                value={form.kind}
                onChange={(e) => set({ kind: e.target.value as AgreementKind })}
                size="small"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="operator">{AGREEMENT_KIND_LABEL.operator}</MenuItem>
                <MenuItem value="company">{AGREEMENT_KIND_LABEL.company}</MenuItem>
              </TextField>
              <TextField
                label="Nome do convênio"
                value={form.tradeName}
                onChange={(e) => set({ tradeName: e.target.value })}
                size="small"
                fullWidth
                required
                placeholder={isOperator ? "Ex.: Amil" : "Ex.: Sincomercio"}
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: { md: "2 / -1" } }}
              />

              {isOperator ? (
                <>
                  <TextField
                    label="Registro ANS"
                    value={form.ansRegistration}
                    onChange={(e) => set({ ansRegistration: digits(e.target.value).slice(0, 6) })}
                    size="small"
                    fullWidth
                    required
                    placeholder="6 dígitos"
                    inputProps={{ maxLength: 6, inputMode: "numeric" }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Código da clínica na operadora"
                    value={form.providerCode}
                    onChange={(e) => set({ providerCode: e.target.value })}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 30 }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ gridColumn: { md: "2 / -1" } }}
                  />
                </>
              ) : (
                <>
                  <TextField
                    select
                    label="Quem paga"
                    value={form.payer}
                    onChange={(e) => set({ payer: e.target.value as FormState["payer"] })}
                    size="small"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="" disabled>Selecione</MenuItem>
                    <MenuItem value="patient">{AGREEMENT_PAYER_LABEL.patient}</MenuItem>
                    <MenuItem value="company">{AGREEMENT_PAYER_LABEL.company}</MenuItem>
                  </TextField>
                  <TextField
                    label="Como comprovar o vínculo"
                    value={form.eligibilityProof}
                    onChange={(e) => set({ eligibilityProof: e.target.value })}
                    size="small"
                    fullWidth
                    placeholder="Ex.: carteirinha do sindicato, holerite do mês"
                    inputProps={{ maxLength: 300 }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ gridColumn: { md: "2 / -1" } }}
                  />
                </>
              )}

              <TextField
                label="Razão social"
                value={form.companyName}
                onChange={(e) => set({ companyName: e.target.value })}
                size="small"
                fullWidth
                inputProps={{ maxLength: 200 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="CNPJ"
                value={form.document}
                onChange={(e) => set({ document: maskDocument(e.target.value) })}
                size="small"
                fullWidth
                inputProps={{ maxLength: 18, inputMode: "numeric" }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Prazo de pagamento (dias)"
                value={form.paymentDeadlineDays}
                onChange={(e) => {
                  const d = digits(e.target.value).slice(0, 3);
                  set({ paymentDeadlineDays: d === "" ? "" : String(Math.min(365, Number(d))) });
                }}
                size="small"
                fullWidth
                inputProps={{ maxLength: 3, inputMode: "numeric" }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Contato"
                value={form.contactName}
                onChange={(e) => set({ contactName: e.target.value })}
                size="small"
                fullWidth
                placeholder={isOperator ? "Quem cuida do credenciamento" : "Ex.: RH, diretoria da associação"}
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Telefone"
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                size="small"
                fullWidth
                inputProps={{ maxLength: 20 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="E-mail"
                type="email"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                size="small"
                fullWidth
                inputProps={{ maxLength: 150 }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Vigência de"
                type="date"
                value={form.startDate}
                onChange={(e) => set({ startDate: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Vigência até"
                type="date"
                value={form.endDate}
                onChange={(e) => set({ endDate: e.target.value })}
                size="small"
                fullWidth
                error={periodInvalid}
                helperText={periodInvalid ? "Anterior ao início" : "Vazio = sem data de fim"}
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => set({ active: e.target.checked })} />}
                label={form.active ? "Ativo" : "Inativo"}
              />

              <TextField
                label="Observações"
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="Regras do acordo que não viram campo (ex.: só titular e cônjuge; máximo de 4 sessões por mês)"
                inputProps={{ maxLength: 1000 }}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "1 / -1", "& textarea": { minHeight: "0 !important", border: "none !important", padding: "0 !important" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setForm(null)} variant="outlined" disabled={saving} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button
            onClick={save}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir convênio"
        message={`Excluir "${deleteTarget?.tradeName ?? ""}"? Só é possível porque ele não tem planos cadastrados.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <HealthPlanModal
        open={planForm !== null}
        plan={planForm?.plan ?? null}
        operators={operatorOptions}
        fixedOperatorId={planForm?.operatorId}
        onClose={() => setPlanForm(null)}
        onSaved={(m) => { showToast(m, "success"); load(); }}
        onError={(m) => showToast(m, "error")}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default HealthPlanOperators;
